import { useState } from 'react';
import { CalendarClock, Car, X } from 'lucide-react';
import {
  ChargeMode,
  formatDate,
  formatEnergy,
  formatPercent,
  formatPower,
  formatTime,
  type LoadpointLive,
  type VehicleInfo,
} from '@ste/shared';
import { useLocks } from '../../api/customerView.ts';
import { useLive } from '../../api/live.ts';
import { useCommands, useSessions, useSite } from '../../api/queries.ts';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SegmentedControl,
  SliderSetting,
  Spinner,
  Stat,
} from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { toDateTimeLocal } from '../../lib/dates.ts';
import { HemsBanner } from '../grid/HemsBanner.tsx';
import { CommandError } from './CommandError.tsx';

export function ChargingPage() {
  const live = useLive();
  const site = useSite().data;
  if (!live) return <Spinner label={t('common.loading')} />;
  const wallboxes = live.loadpoints.filter((lp) => lp.kind === 'wallbox');

  return (
    <div className="space-y-4">
      <PageHeader title={t('charging.title')} />
      <HemsBanner live={live} />
      {wallboxes.map((lp) => (
        <WallboxCard key={lp.id} lp={lp} vehicles={site?.vehicles ?? []} />
      ))}
      <SessionsCard />
    </div>
  );
}

function statusText(lp: LoadpointLive): string {
  if (lp.charging) return t('charging.charging');
  return lp.connected ? t('charging.connected') : t('charging.notConnected');
}

function WallboxCard({ lp, vehicles }: { lp: LoadpointLive; vehicles: VehicleInfo[] }) {
  const commands = useCommands();
  const locks = useLocks();
  const fixedMode = locks.fixedMode(lp.id);
  const modes = ChargeMode.options.map((value) => ({ value, label: t(`mode.${value}`) }));
  const error = [commands.setMode, commands.setLimitSoc, commands.selectVehicle].find(
    (m) => m.isError,
  )?.error;

  return (
    <Card title={lp.title} action={<span className="text-sm text-muted">{statusText(lp)}</span>}>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Stat
              label={t('charging.power')}
              value={formatPower(lp.power)}
              tone={lp.charging ? 'accent' : 'muted'}
            />
            <Stat
              label={t('charging.session')}
              value={lp.sessionEnergy !== undefined ? formatEnergy(lp.sessionEnergy) : '–'}
            />
          </div>

          <div>
            <div className="mb-2 text-sm text-muted">{t('charging.mode')}</div>
            <SegmentedControl
              label={t('charging.mode')}
              options={modes}
              value={lp.mode}
              disabled={commands.setMode.isPending || fixedMode !== undefined}
              onChange={(mode) => commands.setMode.mutate({ id: lp.id, mode })}
            />
            {lp.mode && <p className="mt-2 text-sm text-muted">{t(`mode.hint.${lp.mode}`)}</p>}
            {fixedMode && <p className="mt-2 text-sm text-muted">{t('locks.fixedMode')}</p>}
          </div>
          {error && <CommandError error={error} />}
        </div>

        {/* a disabled fieldset disables every control inside (ADR-0020) */}
        <fieldset disabled={locks.targets} className="min-w-0 space-y-6">
          {locks.targets && <p className="text-sm text-muted">{t('locks.targets')}</p>}
          <VehicleBlock lp={lp} vehicles={vehicles} />
          <SliderSetting
            label={t('charging.limitSoc')}
            value={lp.limitSoc ?? 100}
            min={20}
            max={100}
            step={5}
            format={(v) => formatPercent(v)}
            onCommit={(soc) => commands.setLimitSoc.mutate({ id: lp.id, soc })}
          />
          <PlanBlock lp={lp} />
        </fieldset>
      </div>
    </Card>
  );
}

function VehicleBlock({ lp, vehicles }: { lp: LoadpointLive; vehicles: VehicleInfo[] }) {
  const commands = useCommands();
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-surface-2 p-4">
      <Car className="shrink-0 text-muted" size={28} aria-hidden />
      <div className="min-w-[10rem] flex-1">
        <label className="text-sm text-muted" htmlFor={`vehicle-${lp.id}`}>
          {t('charging.vehicle')}
        </label>
        <select
          id={`vehicle-${lp.id}`}
          className="mt-1 block min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-text"
          value={lp.vehicle?.name ?? ''}
          onChange={(e) =>
            e.target.value && commands.selectVehicle.mutate({ id: lp.id, vehicle: e.target.value })
          }
        >
          {!lp.vehicle && <option value="">{t('charging.noVehicle')}</option>}
          {vehicles.map((v) => (
            <option key={v.name} value={v.name}>
              {v.title}
            </option>
          ))}
        </select>
      </div>
      {lp.vehicle?.soc !== undefined && (
        <div className="text-right">
          <div className="text-2xl font-semibold">{formatPercent(lp.vehicle.soc)}</div>
          {lp.vehicle.range !== undefined && (
            <div className="text-sm text-muted">
              {t('charging.range')} {Math.round(lp.vehicle.range)} km
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanBlock({ lp }: { lp: LoadpointLive }) {
  const commands = useCommands();
  const [editing, setEditing] = useState(false);
  const [time, setTime] = useState(() => {
    const d = new Date(Date.now() + 8 * 3600_000);
    d.setMinutes(0, 0, 0);
    return toDateTimeLocal(d);
  });
  const [soc, setSoc] = useState(80);

  if (lp.plan) {
    const planTime = new Date(lp.plan.time);
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-accent/40 bg-accent-soft p-4">
        <div className="flex items-center gap-3">
          <CalendarClock className="text-accent-text" size={24} aria-hidden />
          <div>
            <div className="font-medium">
              {lp.plan.soc !== undefined
                ? formatPercent(lp.plan.soc)
                : lp.plan.energy !== undefined
                  ? formatEnergy(lp.plan.energy)
                  : ''}{' '}
              {t('charging.planUntil')} {formatTime(planTime)}
            </div>
            <div className="text-sm text-muted">{formatDate(planTime)}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          aria-label={t('common.delete')}
          onClick={() => commands.deletePlan.mutate({ id: lp.id })}
        >
          <X size={20} />
        </Button>
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted">{t('charging.planNone')}</span>
        <Button variant="secondary" className="whitespace-nowrap" onClick={() => setEditing(true)}>
          <CalendarClock size={18} /> {t('charging.planSet')}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="space-y-4 rounded-xl border border-border bg-surface-2 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        commands.setPlan.mutate(
          {
            id: lp.id,
            time: new Date(time).toISOString(),
            soc: lp.vehicle ? soc : undefined,
            energy: lp.vehicle ? undefined : 20,
          },
          { onSuccess: () => setEditing(false) },
        );
      }}
    >
      <label className="block">
        <span className="text-sm text-muted">{t('charging.plan')}</span>
        <input
          type="datetime-local"
          value={time}
          min={toDateTimeLocal(new Date())}
          onChange={(e) => setTime(e.target.value)}
          className="mt-1 block min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-text"
        />
      </label>
      {lp.vehicle && (
        <SliderSetting
          label={t('charging.limitSoc')}
          value={soc}
          min={20}
          max={100}
          step={5}
          format={(v) => formatPercent(v)}
          onCommit={setSoc}
        />
      )}
      {commands.setPlan.isError && <CommandError error={commands.setPlan.error} />}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => setEditing(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={commands.setPlan.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

function SessionsCard() {
  const { data, isLoading, isError } = useSessions();
  return (
    <Card title={t('charging.sessions')}>
      {isLoading && <Spinner label={t('common.loading')} />}
      {isError && <Banner tone="muted" title={t('common.error')} />}
      {data && data.length === 0 && <EmptyState>{t('charging.noSessions')}</EmptyState>}
      {data && data.length > 0 && (
        <ul className="divide-y divide-border">
          {data.slice(0, 10).map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 py-3">
              <div>
                <div className="font-medium">{formatDate(new Date(s.created))}</div>
                <div className="text-sm text-muted">
                  {formatTime(new Date(s.created))} · {s.loadpoint}
                  {s.vehicle && ` · ${s.vehicle}`}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold">{formatEnergy(s.chargedEnergy)}</div>
                {s.solarPercentage !== undefined && (
                  <div className="text-sm text-accent-text">{formatPercent(s.solarPercentage)} Sonne</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
